import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Activity, Box, Grid3X3, Radio } from 'lucide-react';
import MapView, { type MapVisualMode } from '../components/map/MapView.js';
import SearchBar from '../components/search/SearchBar.js';
import FilterPanel from '../components/search/FilterPanel.js';
import PlaceCard from '../components/place/PlaceCard.js';
import PlaceDetails from '../components/place/PlaceDetails.js';
import UserMenu from '../components/user/UserMenu.js';
import TimeOfDayBadge from '../components/map/TimeOfDayBadge.js';
import { useTimeOfDayStore } from '../store/timeOfDayStore.js';
import { useMapStore } from '../store/mapStore.js';
import { useThemeStore } from '../store/themeStore.js';
import { useIsMobile } from '../hooks/useMediaQuery.js';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.js';
import { api } from '../lib/api.js';

export default function MapPage() {
  const {
    places,
    selectedPlace,
    loadingPlaces,
    activeCategory,
    bbox,
    filters,
    setBBox,
    setPlaces,
    setSelectedPlace,
    setLoadingPlaces,
    setActiveCategory,
  } = useMapStore();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toggle } = useThemeStore();
  const timePhase = useTimeOfDayStore((s) => s.phase);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [visualMode, setVisualMode] = useState<MapVisualMode>('raw');
  const [placeTotal, setPlaceTotal] = useState(0);
  const requestController = useRef<AbortController | null>(null);

  useKeyboardShortcuts({
    onSearchFocus: () => {
      const input = document.querySelector('.search-bar input') as HTMLInputElement | null;
      input?.focus();
    },
    onZoomIn: () => {
      const mapEl = document.querySelector('.maplibregl-map') as HTMLElement & { __maplibreMap?: unknown } | null;
      if (mapEl && (mapEl.__maplibreMap as { zoomIn?: () => void } | undefined)?.zoomIn) {
        (mapEl.__maplibreMap as { zoomIn: () => void }).zoomIn();
      }
    },
    onZoomOut: () => {
      const mapEl = document.querySelector('.maplibregl-map') as HTMLElement & { __maplibreMap?: unknown } | null;
      if (mapEl && (mapEl.__maplibreMap as { zoomOut?: () => void } | undefined)?.zoomOut) {
        (mapEl.__maplibreMap as { zoomOut: () => void }).zoomOut();
      }
    },
    onToggleTheme: () => toggle(),
    onEscape: () => setSelectedPlace(null),
    onGoHome: () => navigate('/'),
  });

  const loadPlaces = useCallback(
    async (newBbox: typeof bbox) => {
      if (!newBbox) return;
      setLoadingPlaces(true);
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;
      try {
        const result = await api.places.getByBBox(newBbox, {
          category: activeCategory || undefined,
          openNow: filters.openNow,
          minRating: filters.minRating,
          sortBy: filters.sortBy,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setPlaces(result.data);
          setPlaceTotal(result.total);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setPlaces([]);
          setPlaceTotal(0);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingPlaces(false);
      }
    },
    [activeCategory, filters, setPlaces, setLoadingPlaces],
  );

  useEffect(() => {
    if (bbox) loadPlaces(bbox);
    return () => requestController.current?.abort();
  }, [bbox, loadPlaces]);

  const filteredPlaces = useMemo(() => places, [places]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className={`system-grid system-grid-${visualMode}`} aria-hidden="true" />
      <div className={`atmosphere-overlay atmosphere-${timePhase}`} aria-hidden="true" />
      <div className="system-status" aria-label="System status">
        <span><Radio size={10} /> OSM_NET</span>
        <strong>LIVE</strong>
      </div>
      <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="absolute bottom-3 right-3 z-10 text-[10px] text-cyan-100/70 hover:text-cyan-100">
        © OpenStreetMap contributors
      </a>
      <a
        href="#map-canvas"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-white focus:text-brand-600 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Skip to map
      </a>

      <MapView
        key={visualMode}
        places={filteredPlaces}
        selectedPlace={selectedPlace}
        onPlaceClick={setSelectedPlace}
        onMapMove={setBBox}
        visualMode={visualMode}
      />

      <SearchBar />
      <FilterPanel activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
      <UserMenu />
      <div className="system-telemetry hidden md:flex">
        <TimeOfDayBadge />
        <span className="telemetry-divider" />
        <span>OBJECTS <strong>{String(placeTotal).padStart(3, '0')}</strong></span>
      </div>

      <div className="mode-toolbar" role="toolbar" aria-label="Map rendering mode">
        <span className="mode-toolbar-label"><Activity size={12} /> RENDER</span>
        <button type="button" onClick={() => setVisualMode('raw')} className={visualMode === 'raw' ? 'is-active' : ''} aria-pressed={visualMode === 'raw'}>
          <Box size={14} /> RAW VECTOR
        </button>
        <button type="button" onClick={() => setVisualMode('blueprint')} className={visualMode === 'blueprint' ? 'is-active' : ''} aria-pressed={visualMode === 'blueprint'}>
          <Grid3X3 size={14} /> BLUEPRINT
        </button>
      </div>

      {loadingPlaces && (
        <div
          className="absolute bottom-4 left-4 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg px-4 py-2 text-sm text-gray-500 dark:text-gray-400 dark:border dark:border-gray-700"
          role="status"
          aria-live="polite"
        >
          Loading places...
        </div>
      )}

      <AnimatePresence>
      {!selectedPlace && filteredPlaces.length > 0 && !isMobile && (
        <motion.div
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -14 }}
          transition={{ duration: 0.28 }}
          className="absolute top-24 left-4 z-10 sidebar w-80 max-h-[calc(100vh-180px)]"
          role="navigation"
          aria-label="Places list"
        >
          <div className="p-3 border-b border-gray-100 dark:border-gray-800 font-semibold text-gray-800 dark:text-gray-200 text-sm">
            {filteredPlaces.length} places nearby
          </div>
          {filteredPlaces.slice(0, 50).map((place) => (
            <PlaceCard key={place.id} place={place} onClick={setSelectedPlace} />
          ))}
        </motion.div>
      )}

      {!selectedPlace && filteredPlaces.length > 0 && isMobile && (
        <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bottom-sheet bottom-sheet-collapsed z-20" role="navigation" aria-label="Places list">
          <div className="bottom-sheet-handle" />
          <div className="px-4 pb-2 font-semibold text-gray-800 dark:text-gray-200 text-sm">
            {filteredPlaces.length} places nearby
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '50vh' }}>
            {filteredPlaces.slice(0, 30).map((place) => (
              <PlaceCard key={place.id} place={place} onClick={setSelectedPlace} />
            ))}
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      {selectedPlace && (
        <PlaceDetails place={selectedPlace} onClose={() => setSelectedPlace(null)} />
      )}
    </div>
  );
}
